import { SearchBox, Section } from "../components/primitives.jsx";
import { searchRecords } from "../data/catalog.js";

const capabilities = [
  ["01", "Pattern and anti-pattern bank", "Review a backend by intent, status, risk, category, or side effect.", "/patterns"],
  ["02", "MCP-ready protocol", "Give an agent stable resources, prompts, tools, and JSON indexes.", "/protocol"],
  ["03", "Claude Code skill package", "Install prompts, command examples, and review flows for agent work.", "/skills/README.md"],
  ["04", "Case studies and diagrams", "Evaluate growth risk through bad paths, refactors, and verification gates.", "/case-study"]
];

export function HomePage() {
  return (
    <main>
      <section className="hero">
        <div className="hero__grid" />
        <div className="container hero__inner">
          <span className="lbl">KeepStraight · specification architecture</span>
          <h1 className="hero__title">Keep backend architecture <em>straight</em>.</h1>
          <p className="hero__sub">
            A specification pattern bank for AI-built backends: see what will break as the system
            grows, then follow a refactor sequence with evidence gates.
          </p>
          <div className="hero__search">
            <SearchBox
              records={searchRecords}
              label="Search patterns, risks, examples, and protocols"
              placeholder="Try boundary leaks, test pyramid, agent handoff…"
            />
          </div>
          <div className="hero__ctas">
            <a href="/patterns" className="btn btn--primary">Browse the pattern bank <span className="btn__mark">→</span></a>
            <a href="/protocol" className="btn">Inspect the MCP surface</a>
            <a href="/skills/architecture-review-skill.md" className="btn btn--ghost">Skill package <span className="btn__mark">↗</span></a>
          </div>
        </div>
      </section>

      <Section eyebrow="Full package" title="One specification, several access paths.">
        <p className="section__lede">
          Every practice is a packet: readable guidance, machine-readable metadata, verification
          steps, side effects, and source links live together.
        </p>
        <div className="capability-grid">
          {capabilities.map(([code, title, copy, href]) => (
            <a key={code} className="cell capability" href={href}>
              <div className="cell__chrome">
                <span>{code}</span>
                <span className="lbl lbl--accent">Open →</span>
              </div>
              <h3 className="capability__title">{title}</h3>
              <p className="capability__copy">{copy}</p>
            </a>
          ))}
        </div>
      </Section>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Progressive disclosure</span>
            <h2 className="section__title">Readable first. Queryable underneath.</h2>
            <p className="split__copy">
              Human readers can skim the verdict, open examples, and inspect side effects. Agents
              can fetch stable IDs, resource URIs, tags, severity, verification actions, and
              sources without scraping prose.
            </p>
            <div className="action-map">
              <div><strong>Founder need</strong><span>Know what breaks when an AI-built backend grows.</span></div>
              <div><strong>Developer action</strong><span>Search an anti-pattern and copy a verification checklist.</span></div>
              <div><strong>Agent action</strong><span>Call a tool, read a resource, and produce evidence-backed advice.</span></div>
            </div>
          </div>
          <div className="cell cell--sunken ledger" aria-label="Specification packet preview">
            <div className="cell__chrome"><span>Packet preview</span><span>live</span></div>
            <div className="ledger__row ledger__head"><span>packet</span><span>severity</span><span>action</span></div>
            <div className="ledger__row">
              <span>Boundary leak</span>
              <strong className="tone-crit">critical</strong>
              <a href="/patterns#boundary-leak">verify →</a>
            </div>
            <div className="ledger__row">
              <span>Test hourglass</span>
              <strong className="tone-accent">high</strong>
              <a href="/patterns#test-hourglass">compare →</a>
            </div>
            <div className="ledger__row">
              <span>Agent handoff drift</span>
              <strong className="tone-muted">medium</strong>
              <a href="/protocol#agent-skills">install →</a>
            </div>
          </div>
        </div>
      </section>

      <Section eyebrow="Evidence discipline" title="Patterns carry sources and review gates." compact>
        <div className="source-strip">
          <a href="http://sunnyday.mit.edu/16.355/parnas-criteria.html" target="_blank" rel="noreferrer">Parnas modularity ↗</a>
          <a href="https://martinfowler.com/bliki/TestPyramid.html" target="_blank" rel="noreferrer">Fowler test pyramid ↗</a>
          <a href="https://testing.googleblog.com/2015/04/just-say-no-to-more-end-to-end-tests.html" target="_blank" rel="noreferrer">Google E2E guidance ↗</a>
          <a href="https://sre.google/sre-book/service-level-objectives/" target="_blank" rel="noreferrer">Google SRE SLOs ↗</a>
          <a href="https://modelcontextprotocol.io/specification/2025-11-25" target="_blank" rel="noreferrer">MCP specification ↗</a>
        </div>
      </Section>
    </main>
  );
}
